// services/dynamicsApi.js
import { dynamicsBaseUrl, accountNavigationExpands } from '../config/config';
import { getApiClient } from './apiClient';
import { ACCOUNT_TYPES } from '../config/constants';

/**
 * Obtient un token d'accès valide.
 * Délègue à l'ApiClient qui centralise la logique MSAL :
 *   silent (cache/refresh token) → popup fallback.
 * Le paramètre msalInstance est conservé pour compatibilité mais ignoré
 * car l'ApiClient possède déjà l'instance MSAL.
 */
export const getAccessToken = async (_msalInstance) => {
  const apiClient = getApiClient();
  return apiClient.acquireToken();
};

// Champs à récupérer pour les contacts
const contactFields = [
  'contactid',
  'createdon',
  'dimsi_nbofemployees',
  'fullname',
  // 'dimsi_partnerid'
];

// Champs à récupérer pour le compte parent
const parentAccountFields = [
  'name',
  'industrycode',
//   'numberofemployees',
  'dimsi_typecode',
  'dimsi_regionofthecountry',
  '_dimsi_zoneid_value',
  '_dimsi_countryid_value',
  '_dimsi_partnerid_value',
  'dimsi_productssubscribed',
  'dimsi_contractmrrpintranet',
  'dimsi_contractmrrpteams',
  'dimsi_Deployment_partner',
  'dimsi_healthscore'
];

// Helper: build nested expand fragments for account navigation properties
// Returns a string like "nav1($select=name),nav2($select=name)" or empty string
const buildNestedExpands = (navs = accountNavigationExpands) => {
  if (!navs || navs.length === 0) return '';
  const parts = navs.map(nav => `${nav}($select=name)`);
  return parts.join(',');
};

// Filtres pour les contacts
const contactProspectFilters = [
  'statecode ne 1', // Actif
  'parentcustomerid_account ne null',
  `parentcustomerid_account/dimsi_typecode eq ${ACCOUNT_TYPES.PROSPECT}` // Type = Prospect
];

// Récupérer la liste des contacts de type Prospect
export const fetchContactsProspects = async (token, lastContactId = null, top = 50) => {
  const selectFields = contactFields.join(',');
  const expandFields = parentAccountFields.join(',');
  // build nested expands for account navigation properties (e.g. partner lookups)
  const nestedExpands = buildNestedExpands();
  const filterFields = contactProspectFilters.join(' and ');

  // Ajouter un filtre pour la pagination basée sur l'ID si lastContactId est fourni
  let paginationFilter = filterFields;
  if (lastContactId) {
    paginationFilter += ` and contactid gt '${lastContactId}'`;
  }

  const innerExpandPart = nestedExpands ? `;$expand=${nestedExpands}` : '';
  const url = `${dynamicsBaseUrl}/contacts?$top=${top}&$select=${selectFields}&$expand=parentcustomerid_account($select=${expandFields}${innerExpandPart})&$filter=${paginationFilter}&$orderby=createdon desc`;

  const apiClient = getApiClient();
  const data = await apiClient.fetchJson(url, token);
  return data.value;
};

// Récupérer les contacts similaires de type Signed
export const fetchSimilarContactsSigned = async (token, criteria) => {
  let filter = `parentcustomerid_account/dimsi_typecode eq ${ACCOUNT_TYPES.SIGNED} and `; // Type = Signed
  if (criteria.industrycode) filter += `parentcustomerid_account/industrycode eq ${criteria.industrycode} and `;
  if (criteria._dimsi_countryid_value) filter += `parentcustomerid_account/_dimsi_countryid_value eq ${criteria._dimsi_countryid_value} and `;
  if (criteria._dimsi_zoneid_value) filter += `parentcustomerid_account/_dimsi_zoneid_value eq ${criteria._dimsi_zoneid_value} and `;
  if (criteria.dimsi_nbofemployees) filter += `dimsi_nbofemployees eq '${criteria.dimsi_nbofemployees}' and `;
  filter = filter.slice(0, -5); // Enlever le dernier ' and '

  const selectFields = contactFields.join(',');
  const expandFields = parentAccountFields.join(',');
  const nestedExpandsSimilar = buildNestedExpands();
  const innerExpandPart = nestedExpandsSimilar ? `;$expand=${nestedExpandsSimilar}` : '';
  const url = `${dynamicsBaseUrl}/contacts?$select=${selectFields}&$expand=parentcustomerid_account($select=${expandFields}${innerExpandPart})&$filter=${filter}&$top=500`;

  const apiClient = getApiClient();
  const data = await apiClient.fetchJson(url, token);
  return data.value;
};

// Récupérer les contacts signés avec un filtre OData personnalisé (pour recherche avancée)
export const fetchSimilarContactsSignedAdvanced = async (token, customFilter) => {
  const selectFields = contactFields.join(',');
  const expandFields = parentAccountFields.join(',');
  const nestedExpandsAdvanced = buildNestedExpands();
  const innerExpandPart = nestedExpandsAdvanced ? `;$expand=${nestedExpandsAdvanced}` : '';
  const url = `${dynamicsBaseUrl}/contacts?$select=${selectFields}&$expand=parentcustomerid_account($select=${expandFields}${innerExpandPart})&$filter=${customFilter}&$top=500`;

  const apiClient = getApiClient();
  const data = await apiClient.fetchJson(url, token);
  return data.value;
};

// Fonction pour récupérer la liste des pays depuis Dynamics 365 (dimsi_countries)
export const fetchCountryOptions = async (accessToken) => {
  const url = `${dynamicsBaseUrl}/dimsi_countries?$select=dimsi_countryid,dimsi_name&$orderby=dimsi_name asc&$top=500`;
  try {
    const apiClient = getApiClient();
    const data = await apiClient.fetchJson(url, accessToken);
    return (data.value || []).map(c => ({
      id: c.dimsi_countryid,
      name: c.dimsi_name,
    }));
  } catch (error) {
    console.error('Error fetching country options:', error);
    throw error;
  }
};

// Fonction pour récupérer la liste des zones depuis Dynamics 365 (dimsi_zones)
export const fetchZoneOptions = async (accessToken) => {
  const url = `${dynamicsBaseUrl}/dimsi_zones?$select=dimsi_zoneid,dimsi_name&$orderby=dimsi_name asc&$top=500`;
  try {
    const apiClient = getApiClient();
    const data = await apiClient.fetchJson(url, accessToken);
    return (data.value || []).map(z => ({
      id: z.dimsi_zoneid,
      name: z.dimsi_name,
    }));
  } catch (error) {
    console.error('Error fetching zone options:', error);
    throw error;
  }
};

// Fonction pour récupérer la liste des partenaires depuis Dynamics 365
// Les partenaires sont des comptes avec type Partner
export const fetchPartnerOptions = async (accessToken) => {
  const url = `${dynamicsBaseUrl}/accounts?$select=accountid,name&$filter=dimsi_typecode eq ${ACCOUNT_TYPES.PARTNER}&$orderby=name asc&$top=500`;
  try {
    const apiClient = getApiClient();
    const data = await apiClient.fetchJson(url, accessToken);
    return (data.value || []).map(p => ({
      id: p.accountid,
      name: p.name,
    }));
  } catch (error) {
    console.error('Error fetching partner options:', error);
    throw error;
  }
};

// Fonction pour récupérer les options d'industrie depuis Dynamics 365
export const fetchIndustryOptions = async (accessToken) => {
  const entityLogicalName = 'account';
  const attributeLogicalName = 'industrycode';

  const url = `${dynamicsBaseUrl}/EntityDefinitions(LogicalName='${entityLogicalName}')/Attributes(LogicalName='${attributeLogicalName}')/Microsoft.Dynamics.CRM.PicklistAttributeMetadata?$expand=OptionSet`;

  try {
    const apiClient = getApiClient();
    const data = await apiClient.fetchJson(url, accessToken);
    return data.OptionSet.Options || [];
  } catch (error) {
    console.error('Error fetching industry options:', error);
    throw error;
  }
};

// Fonction pour récupérer le nom de l'industrie par code
export const fetchIndustryLabel = async (token, industryCode) => {
  if (!industryCode) return null;
  try {
    const options = await fetchIndustryOptions(token);
    const option = options.find(opt => opt.Value === industryCode);
    return option ? option.Label.UserLocalizedLabel.Label : null;
  } catch (error) {
    console.error('Error fetching industry label:', error);
    return null;
  }
};

// Fonction pour récupérer le nom du pays par ID
export const fetchCountryName = async (token, countryId) => {
  if (!countryId) return null;
  const url = `${dynamicsBaseUrl}/dimsi_countries(${countryId})?$select=dimsi_name`;
  try {
    const apiClient = getApiClient();
    const data = await apiClient.fetchJson(url, token);
    return data.dimsi_name;
  } catch (error) {
    console.error('Error fetching country name:', error);
    return null;
  }
};

// Fonction pour récupérer le nom de la zone par ID
export const fetchZoneName = async (token, zoneId) => {
  if (!zoneId) return null;
  const url = `${dynamicsBaseUrl}/dimsi_zones(${zoneId})?$select=dimsi_name`;
  try {
    const apiClient = getApiClient();
    const data = await apiClient.fetchJson(url, token);
    return data.dimsi_name;
  } catch (error) {
    console.error('Error fetching zone name:', error);
    return null;
  }
};

// Fonction pour récupérer les options de Health Score depuis Dynamics 365
export const fetchHealthScoreOptions = async (accessToken) => {
  const entityLogicalName = 'account';
  const attributeLogicalName = 'dimsi_healthscore';

  const url = `${dynamicsBaseUrl}/EntityDefinitions(LogicalName='${entityLogicalName}')/Attributes(LogicalName='${attributeLogicalName}')/Microsoft.Dynamics.CRM.PicklistAttributeMetadata?$expand=OptionSet`;

  try {
    const apiClient = getApiClient();
    const data = await apiClient.fetchJson(url, accessToken);
    return data.OptionSet.Options || [];
  } catch (error) {
    console.error('Error fetching health score options:', error);
    throw error;
  }
};

// Fonction pour récupérer le libellé du Health Score par code
export const fetchHealthScoreLabel = async (token, healthScoreCode) => {
  if (!healthScoreCode && healthScoreCode !== 0) return null;
  try {
    const options = await fetchHealthScoreOptions(token);
    const option = options.find(opt => opt.Value === healthScoreCode);
    return option ? option.Label.UserLocalizedLabel.Label : null;
  } catch (error) {
    console.error('Error fetching health score label:', error);
    return null;
  }
};